import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import Device from "../../models/Device";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import { isUiModeProductAdmin, uiModeWatcher } from "../../uiMode";

export default function KnxDetails(props: { device: Device }) {
  const { device } = props;

  const scrollDivRef = useRef<HTMLDivElement>(null);
  const isRestoringScrollRef = useRef(false);
  const chapterScrollTopMapRef = useRef(new Map<string, number>());

  const currentKnxChapter = device.currentKnxChapter;
  const subscribeToChapter = useCallback(
    (onStoreChange: () => void) => {
      const handle = currentKnxChapter.onChange.subscribe(onStoreChange);
      return () => currentKnxChapter.onChange.unsubscribe(handle);
    },
    [currentKnxChapter],
  );
  const getChapterSnapshot = useCallback(() => currentKnxChapter.value, [currentKnxChapter]);
  // The hierarchy selects the initial chapter in an earlier sibling's effect.
  // Recheck the snapshot on subscription so that initial selection cannot be missed.
  const knxChapter = useSyncExternalStore(subscribeToChapter, getChapterSnapshot);

  useOnChangeWatcher(knxChapter);
  useOnChangeWatcher(uiModeWatcher);

  useLayoutEffect(() => {
    if (!knxChapter || !scrollDivRef.current) {
      return;
    }

    const chapterKey = String(knxChapter.chapterId);
    const desiredScrollTop = chapterScrollTopMapRef.current.get(chapterKey) ?? 0;
    const applyScrollTop = () => {
      if (!scrollDivRef.current || scrollDivRef.current.scrollTop === desiredScrollTop) {
        return;
      }

      isRestoringScrollRef.current = true;
      scrollDivRef.current.scrollTop = desiredScrollTop;
      requestAnimationFrame(() => {
        isRestoringScrollRef.current = false;
      });
    };

    applyScrollTop();
    const rafId = requestAnimationFrame(applyScrollTop);
    return () => {
      cancelAnimationFrame(rafId);
      isRestoringScrollRef.current = false;
    };
  }, [knxChapter, knxChapter?.content]);

  if (!knxChapter?.content) {
    return (
      <div
        className="estadaColumn left top w100 h100"
        style={{ overflow: "auto", padding: "10px", paddingTop: "20px" }}
      ></div>
    );
  }

  const headerText = device.substitutePropertyPlaceholders(knxChapter.detailHeader || "", knxChapter.instanceId);

  return (
    <div className="estadaColumn left top w100 h100" style={{ overflow: "hidden", flexGrow: "1", minWidth: "180px" }}>
      <div
        key={`knxChapterHeader_${knxChapter.chapterId}`}
        className="ObjectPropertiesHeader"
        style={{ marginBottom: "0px" }}
      >
        {headerText}
        {isUiModeProductAdmin() && (
          <div style={{ fontWeight: "normal", marginLeft: "30px", color: "black", fontSize: "12px" }}>
            {" instanceId=" + knxChapter.instanceId}
          </div>
        )}
      </div>

      <div
        key={`knxChapterContent_${knxChapter.chapterId}`}
        className="estadaColumn left top w100 h100"
        style={{ overflow: "auto", padding: "10px", paddingTop: "0px" }}
        onScroll={(e) => {
          if (isRestoringScrollRef.current || !e.nativeEvent.isTrusted) {
            return;
          }
          chapterScrollTopMapRef.current.set(String(knxChapter.chapterId), (e.target as HTMLDivElement).scrollTop);
        }}
        ref={scrollDivRef}
      >
        {knxChapter.content}
      </div>
    </div>
  );
}
